import React from "react";
import { Popover, Tooltip } from "antd";
import { localizeByKeyStatic } from "components/service";
import CommunicationConst from "../communication-const";
import CommunicationOriginalEmailField from "./communication-original-email-field";
import Icons from "components/icons";
import {
  getCommunicationConfidenceTone,
  normalizeCommunicationConfidence,
} from "../helpers/communication-confidence-helper";
import type { TCommunicationTask } from "../helpers/communication-types-helper";

interface ICommunicationInfoFieldProps {
  task: TCommunicationTask;
}

const workStateLabelMap: Record<TCommunicationTask["work_state"]["status"], string> = {
  untouched: "COMMUNICATION.WorkState_Untouched",
  in_progress: "COMMUNICATION.WorkState_InProgress",
  approved: "COMMUNICATION.WorkState_Approved",
};

const getCountLabel = (
  count: number,
  singularKey: string,
  pluralKey: string
): string =>
  localizeByKeyStatic(count === 1 ? singularKey : pluralKey, { count });

const formatStandNumberLabel = (value?: string | null): string => {
  const normalizedValue = typeof value === "string" ? value.trim() : "";

  if (!normalizedValue) {
    return "\u2014";
  }

  const withoutPrefix = normalizedValue.replace(/^(\u2116\s*|N\s*|#\s*)/iu, "").trim();

  return `\u2116 ${withoutPrefix || normalizedValue}`;
};

const getEmailSnippet = (task: TCommunicationTask): string => {
  const rawSnippet = task.ai_summary?.trim() || task.original_body?.trim() || "";

  return rawSnippet.replace(/\s+/g, " ").trim();
};

const stopRowNavigation = (event: React.MouseEvent<HTMLElement>) => {
  event.stopPropagation();
};

const CommunicationInfoField: React.FC<ICommunicationInfoFieldProps> = ({ task }) => {
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const confidence = normalizeCommunicationConfidence(task.confidence);
  const confidenceTone = getCommunicationConfidenceTone(confidence);
  const requestCount = task.request_items.length;
  const requestGroupCount = task.request_groups.length;
  const openQuestions = task.questions.filter((question) => question.status === "open").length;
  const workStateLabel = localizeByKeyStatic(workStateLabelMap[task.work_state.status]);
  const snippet = getEmailSnippet(task);
  const shouldShowWorkState = task.work_state.status !== "untouched";
  const hasPreview = Boolean(task.original_body?.trim());
  const senderEmailHref = task.sender_email?.trim()
    ? `mailto:${task.sender_email.trim()}`
    : undefined;
  const standContextLabel =
    requestGroupCount > 1
      ? getCountLabel(
        requestGroupCount,
        "COMMUNICATION.Info_StandGroupsCount",
        "COMMUNICATION.Info_StandGroupsCountPlural"
      )
      : formatStandNumberLabel(task.stand_number);
  const contextSummary = [ task.company_name || "\u2014", standContextLabel ].filter(Boolean).join(" \u2022 ");
  const itemCountSummary = getCountLabel(
    requestCount,
    "COMMUNICATION.Info_ItemsCount",
    "COMMUNICATION.Info_ItemsCountPlural"
  );
  const countsSummary = requestGroupCount > 1
    ? [
      itemCountSummary,
      getCountLabel(
        requestGroupCount,
        "COMMUNICATION.Info_StandGroupsCount",
        "COMMUNICATION.Info_StandGroupsCountPlural"
      ),
    ].join(" \u2022 ")
    : itemCountSummary;
  const attentionFlags = [
    task.status === CommunicationConst.statuses.UNIDENTIFIED
      ? {
        key: "match",
        tone: "warning",
        label: localizeByKeyStatic("COMMUNICATION.Info_NeedsMatch"),
      }
      : null,
    openQuestions > 0
      ? {
        key: "clarifications",
        tone: "warning",
        label: localizeByKeyStatic(
          openQuestions === 1
            ? "COMMUNICATION.Info_OpenClarification"
            : "COMMUNICATION.Info_OpenClarificationPlural",
          { count: openQuestions }
        ),
      }
      : null,
  ].filter(Boolean) as Array<{
    key: string;
    tone: "warning" | "info";
    label: string;
  }>;

  return (
    <div className="communication-info-field">
      <div className="communication-info-field__header">
        <Tooltip title={task.subject}>
          <strong className="communication-info-field__subject">{task.subject || "\u2014"}</strong>
        </Tooltip>

        <span
          className={`communication-info-field__confidence communication-info-field__confidence--${confidenceTone}`}
          title={`${localizeByKeyStatic("TABLES.Column_Title_Confidence")}: ${confidence}%`}
        >
          {`${confidence}%`}
        </span>
      </div>

      {senderEmailHref ? (
        <Tooltip title={task.sender}>
          <a
            className="communication-info-field__sender"
            href={senderEmailHref}
            onMouseDown={stopRowNavigation}
          >
            {task.sender}
          </a>
        </Tooltip>
      ) : (
        <Tooltip title={task.sender}>
          <span className="communication-info-field__sender">{task.sender}</span>
        </Tooltip>
      )}

      <span className="communication-info-field__context" title={contextSummary}>
        {contextSummary}
      </span>

      {snippet && (
        <span className="communication-info-field__snippet">{snippet}</span>
      )}

      {(task.demo_badge_key || shouldShowWorkState) && (
        <div className="communication-info-field__meta">
          {task.demo_badge_key && (
            <span className="communication-info-field__demo-badge">
              {localizeByKeyStatic(task.demo_badge_key)}
            </span>
          )}
          {shouldShowWorkState && (
            <span
              className={`communication-info-field__state communication-info-field__state--${task.work_state.status}`}
              title={localizeByKeyStatic("COMMUNICATION.WorkState_Label")}
            >
              {workStateLabel}
            </span>
          )}
        </div>
      )}

      {attentionFlags.length > 0 && (
        <div className="communication-info-field__flags">
          {attentionFlags.map((flag) => (
            <span
              key={flag.key}
              className={`communication-info-field__flag communication-info-field__flag--${flag.tone}`}
            >
              {flag.label}
            </span>
          ))}
        </div>
      )}

      <div className="communication-info-field__footer">
        <span className="communication-info-field__summary">{countsSummary}</span>

        {hasPreview && (
          <Popover
            trigger="click"
            open={isPreviewOpen}
            onOpenChange={setIsPreviewOpen}
            placement="rightTop"
            overlayClassName="communication-info-field__popover-overlay"
            getPopupContainer={(trigger) =>
              trigger.closest(".page-communication") || document.body
            }
            content={(
              <div className="communication-info-field__email-tooltip">
                <CommunicationOriginalEmailField
                  originalBody={task.original_body}
                  equipmentChanges={task.final_changes.equipment_changes}
                  isExpanded={true}
                />
              </div>
            )}
          >
            <button
              type="button"
              className={`communication-info-field__preview-trigger${isPreviewOpen ? " communication-info-field__preview-trigger--active" : ""}`}
              onMouseDown={stopRowNavigation}
            >
              <Icons.Document />
              <span>{localizeByKeyStatic("COMMUNICATION.Section_AIContextSecondary")}</span>
            </button>
          </Popover>
        )}
      </div>
    </div>
  );
};

export default CommunicationInfoField;
